import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
    FaUsers, FaUserShield, FaPlus, FaTrash, FaSearch, 
    FaEdit, FaSave, FaTimes, FaEye, FaEyeSlash, FaUserTie,
    FaExclamationTriangle
} from 'react-icons/fa';
import api from '../../api/axios';
import { toast } from 'react-toastify';

const AdminUsers = () => {
    const [activeTab, setActiveTab] = useState('manager');
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    // State untuk Modal & Form
    const [showModal, setShowModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', action: null });
    
    // Split State Hide/Show Password
    const [showPassword, setShowPassword] = useState(false); 
    const [showConfirmPassword, setShowConfirmPassword] = useState(false); 
    
    const [selectedUuid, setSelectedUuid] = useState(null);
    
    const [formData, setFormData] = useState({
        name: '', email: '', username: '', password: '',
        confirmPassword: '', role: 'manager', phone: ''
    });

    useEffect(() => {
        fetchUsers();
    }, [activeTab]);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const endpoint = activeTab === 'manager' ? '/users/managers' : '/users/admins';
            const response = await api.get(endpoint);
            setUsers(response.data);
        } catch (error) {
            toast.error("Gagal mengambil data pengguna", { style: { fontSize: '0.85rem' } });
        } finally {
            setLoading(false);
        }
    };

    const handleAddClick = () => {
        setIsEditMode(false);
        setShowPassword(false);
        setShowConfirmPassword(false);
        setFormData({
            name: '', email: '', username: '', password: '', confirmPassword: '',
            role: activeTab === 'manager' ? 'manager' : 'admin_timbang', 
            phone: ''
        });
        setShowModal(true);
    };

    const handleEditClick = (user) => {
        setIsEditMode(true);
        setShowPassword(false);
        setShowConfirmPassword(false);
        setSelectedUuid(user.uuid);
        setFormData({
            name: user.name,
            email: user.email,
            username: user.username,
            password: '', 
            confirmPassword: '',
            role: user.role,
            phone: user.phone || ''
        });
        setShowModal(true);
    };

    const handleFormChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.password !== formData.confirmPassword) {
            return toast.error("Password dan Konfirmasi Password tidak cocok!", { style: { fontSize: '0.85rem' } });
        }

        const toastId = toast.loading(isEditMode ? "Menyimpan perubahan..." : "Membuat akun baru...", { style: { fontSize: '0.85rem' } });

        try {
            if (isEditMode) {
                await api.patch(`/users/${selectedUuid}`, formData);
                toast.update(toastId, { render: "Akun berhasil diperbarui", type: "success", isLoading: false, autoClose: 3000 });
            } else {
                const endpoint = formData.role === 'manager' ? '/users/managers' : '/users/admins';
                await api.post(endpoint, formData);
                toast.update(toastId, { render: "Akun baru berhasil dibuat", type: "success", isLoading: false, autoClose: 3000 });
            }
            setShowModal(false);
            fetchUsers();
        } catch (error) {
            toast.update(toastId, { render: error.response?.data?.msg || "Terjadi kesalahan", type: "error", isLoading: false, autoClose: 3000 });
        }
    };

    const handleDeleteClick = (uuid) => {
        setConfirmModal({
            show: true,
            title: 'Hapus Pengguna',
            message: 'Apakah Anda yakin ingin menghapus akun ini? Tindakan ini tidak dapat dibatalkan.',
            action: async () => {
                setConfirmModal({ show: false, title: '', message: '', action: null });
                const toastId = toast.loading("Menghapus akun...", { style: { fontSize: '0.85rem' } });
                try {
                    await api.delete(`/users/${uuid}`);
                    toast.update(toastId, { render: "Pengguna berhasil dihapus", type: "success", isLoading: false, autoClose: 3000 });
                    fetchUsers();
                } catch (error) {
                    toast.update(toastId, { render: error.response?.data?.msg || "Gagal menghapus", type: "error", isLoading: false, autoClose: 3000 });
                }
            }
        });
    };

    const filteredUsers = users.filter(u => 
        u.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.username.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getRoleBadge = (role) => {
        switch (role) {
            case 'manager': return <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25 px-2 py-1" style={{ fontSize: '0.7rem' }}>MANAGER TIM</span>;
            case 'admin_timbang': return <span className="badge bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25 px-2 py-1" style={{ fontSize: '0.7rem' }}>ADMIN TIMBANG</span>;
            case 'admin_medali': return <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 px-2 py-1" style={{ fontSize: '0.7rem' }}>ADMIN MEDALI</span>;
            case 'admin_koordinator': return <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 px-2 py-1" style={{ fontSize: '0.7rem' }}>KOORDINATOR</span>;
            case 'super_admin': return <span className="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25 px-2 py-1" style={{ fontSize: '0.7rem' }}>SUPER ADMIN</span>;
            default: return <span className="badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25 px-2 py-1" style={{ fontSize: '0.7rem' }}>{role}</span>;
        }
    };

    return (
        <div className="container-fluid p-0 d-flex flex-column h-100 position-relative animate-fade-in" style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
            
            {/* HEADER */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
                <div>
                    <h5 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                        <FaUsers className="text-primary" /> Manajemen Pengguna
                    </h5>
                    <p className="text-muted m-0" style={{ fontSize: '0.85rem' }}>
                        Kelola akses dan akun untuk manajer tim serta administrator sistem.
                    </p>
                </div>
            </div>

            {/* TAB NAVIGASI & KONTROL (Pencarian & Tambah) */}
            <div className="d-flex flex-column flex-xl-row justify-content-between align-items-xl-center mb-4 gap-3">
                <div className="bg-white rounded p-1 border shadow-sm d-flex gap-1" style={{ width: 'max-content' }}>
                    <button className={`btn btn-sm px-3 py-1 fw-bold transition-all ${activeTab === 'manager' ? 'btn-primary shadow-sm' : 'btn-white text-muted hover-bg-light'}`} style={{ fontSize: '0.8rem' }} onClick={() => setActiveTab('manager')}>
                        <FaUserTie className="me-2" /> Akun Manager
                    </button>
                    <button className={`btn btn-sm px-3 py-1 fw-bold transition-all ${activeTab === 'admin' ? 'btn-primary shadow-sm' : 'btn-white text-muted hover-bg-light'}`} style={{ fontSize: '0.8rem' }} onClick={() => setActiveTab('admin')}>
                        <FaUserShield className="me-2" /> Akun Admin
                    </button>
                </div>

                <div className="d-flex gap-2 flex-wrap align-items-center">
                    <div className="input-group input-group-sm shadow-sm rounded overflow-hidden border bg-white" style={{ width: '260px' }}>
                        <span className="input-group-text bg-transparent border-0 pe-1"><FaSearch className="text-muted"/></span>
                        <input 
                            type="text" 
                            className="form-control border-0 shadow-none bg-transparent" 
                            placeholder="Cari nama, email, username..." 
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ fontSize: '0.8rem' }}
                        />
                    </div>
                    <button className="btn btn-primary btn-sm px-3 py-1 fw-bold shadow-sm d-flex align-items-center gap-2 hover-scale" style={{ fontSize: '0.8rem', height: '31px' }} onClick={handleAddClick}>
                        <FaPlus /> Tambah Akun
                    </button>
                </div>
            </div>

            {/* TABEL DATA PENGGUNA */}
            <div className="card border-0 shadow-sm rounded bg-white flex-grow-1 mb-4">
                <div className="card-body p-0">
                    <div className="table-responsive custom-scrollbar" style={{ maxHeight: 'calc(100vh - 240px)' }}>
                        <table className="table table-hover align-middle mb-0">
                            <thead className="bg-light sticky-top" style={{ zIndex: 10 }}>
                                <tr className="text-muted text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                                    <th className="ps-4 py-3">Nama & Username</th>
                                    <th>Kontak (Email & WA)</th>
                                    <th>Role Akses</th>
                                    <th className="text-center pe-4">Aksi</th>
                                </tr>
                            </thead>
                            <tbody className="border-top-0">
                                {loading ? (
                                    <tr><td colSpan="4" className="text-center py-5"><div className="spinner-border text-primary spinner-border-sm"></div></td></tr>
                                ) : filteredUsers.length > 0 ? (
                                    filteredUsers.map((user, idx) => (
                                        <tr key={idx} className="transition-all hover-bg-light">
                                            <td className="ps-4 py-3">
                                                <div className="fw-bold text-dark" style={{ fontSize: '0.85rem' }}>{user.name}</div>
                                                <div className="text-muted" style={{ fontSize: '0.75rem' }}>@{user.username}</div>
                                            </td>
                                            <td>
                                                <div className="text-dark fw-medium" style={{ fontSize: '0.85rem' }}>{user.email}</div>
                                                <div className="text-muted" style={{ fontSize: '0.75rem' }}>{user.phone || '-'}</div>
                                            </td>
                                            <td>{getRoleBadge(user.role)}</td>
                                            <td className="text-center pe-4">
                                                {/* PERBAIKAN: Tombol Aksi dibuat lebih responsif dengan Icon + Teks */}
                                                <div className="d-flex justify-content-center gap-2">
                                                    <button 
                                                        className="btn btn-sm btn-light border text-primary fw-bold d-flex align-items-center gap-2 px-3 py-1 shadow-sm hover-scale" 
                                                        onClick={() => handleEditClick(user)} 
                                                        style={{ fontSize: '0.75rem' }}
                                                    >
                                                        <FaEdit /> Edit
                                                    </button>
                                                    <button 
                                                        className="btn btn-sm btn-light border text-danger fw-bold d-flex align-items-center gap-2 px-3 py-1 shadow-sm hover-scale" 
                                                        onClick={() => handleDeleteClick(user.uuid)} 
                                                        style={{ fontSize: '0.75rem' }}
                                                    >
                                                        <FaTrash /> Hapus
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="4" className="text-center py-5">
                                            <FaSearch size={30} className="text-muted opacity-25 mb-3" />
                                            <h6 className="text-muted fw-bold">Data Tidak Ditemukan</h6>
                                            <p className="text-muted m-0" style={{ fontSize: '0.85rem' }}>Coba gunakan kata kunci pencarian yang lain.</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* PORTAL UNTUK MODAL TAMBAH/EDIT */}
            {showModal && createPortal(
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ zIndex: 1100, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(3px)' }}>
                    <div className="card border-0 shadow-lg animate-pop-in rounded" style={{ width: '90%', maxWidth: '500px' }}>
                        <div className="card-header bg-white p-4 pb-3 border-bottom-0 d-flex justify-content-between align-items-center">
                            <h6 className="fw-bold text-dark m-0">{isEditMode ? 'Edit Pengguna' : 'Tambah Pengguna Baru'}</h6>
                            <button type="button" className="btn btn-sm btn-light rounded-circle hover-scale text-muted" onClick={() => setShowModal(false)}><FaTimes /></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="card-body p-4 pt-0">
                                <div className="mb-3">
                                    <label className="form-label text-muted fw-bold mb-1" style={{ fontSize: '0.75rem' }}>Nama Lengkap</label>
                                    <input type="text" name="name" className="form-control form-control-sm rounded bg-light border-0 px-3 py-2" placeholder="Masukkan nama lengkap" required value={formData.name} onChange={handleFormChange} style={{ fontSize: '0.8rem' }} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label text-muted fw-bold mb-1" style={{ fontSize: '0.75rem' }}>Email</label>
                                    <input type="email" name="email" className="form-control form-control-sm rounded bg-light border-0 px-3 py-2" placeholder="contoh@email.com" required value={formData.email} onChange={handleFormChange} style={{ fontSize: '0.8rem' }} />
                                </div>
                                <div className="row g-3 mb-3">
                                    <div className="col-6">
                                        <label className="form-label text-muted fw-bold mb-1" style={{ fontSize: '0.75rem' }}>Username</label>
                                        <input type="text" name="username" className="form-control form-control-sm rounded bg-light border-0 px-3 py-2" placeholder="username" required value={formData.username} onChange={handleFormChange} style={{ fontSize: '0.8rem' }} />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label text-muted fw-bold mb-1" style={{ fontSize: '0.75rem' }}>No. WhatsApp</label>
                                        <input type="text" name="phone" className="form-control form-control-sm rounded bg-light border-0 px-3 py-2" placeholder="0812..." value={formData.phone} onChange={handleFormChange} style={{ fontSize: '0.8rem' }} />
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label text-muted fw-bold mb-1" style={{ fontSize: '0.75rem' }}>Role / Hak Akses</label>
                                    <select name="role" className="form-select form-select-sm rounded bg-light border-0 px-3 py-2" value={formData.role} onChange={handleFormChange} disabled={isEditMode && formData.role === 'super_admin'} style={{ fontSize: '0.8rem' }}>
                                        <option value="manager">Manager Tim</option>
                                        <option value="admin_timbang">Admin Penimbangan</option>
                                        <option value="admin_medali">Admin Medali & Juara</option>
                                        <option value="admin_koordinator">Admin Koordinator Lapangan</option>
                                        {isEditMode && formData.role === 'super_admin' && <option value="super_admin">Super Admin</option>}
                                    </select>
                                </div>

                                <hr className="my-4 text-muted opacity-10" />

                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label text-muted fw-bold mb-1" style={{ fontSize: '0.75rem' }}>
                                            {isEditMode ? 'Password Baru (Opsional)' : 'Password'}
                                        </label>
                                        <div className="input-group input-group-sm bg-light rounded overflow-hidden">
                                            <input 
                                                type={showPassword ? "text" : "password"} 
                                                name="password" 
                                                className="form-control border-0 bg-transparent px-3 py-2 shadow-none" 
                                                placeholder="******" 
                                                required={!isEditMode} 
                                                value={formData.password} 
                                                onChange={handleFormChange} 
                                                style={{ fontSize: '0.8rem' }}
                                            />
                                            <button className="btn btn-light text-muted border-0 bg-transparent px-3" type="button" onClick={() => setShowPassword(!showPassword)}>
                                                {showPassword ? <FaEyeSlash size={14}/> : <FaEye size={14}/>}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label text-muted fw-bold mb-1" style={{ fontSize: '0.75rem' }}>Konfirmasi Password</label>
                                        <div className={`input-group input-group-sm bg-light rounded overflow-hidden border ${formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword ? 'border-danger' : 'border-0'}`}>
                                            <input 
                                                type={showConfirmPassword ? "text" : "password"} 
                                                name="confirmPassword" 
                                                className="form-control border-0 bg-transparent px-3 py-2 shadow-none" 
                                                placeholder="******" 
                                                required={formData.password.length > 0} 
                                                value={formData.confirmPassword} 
                                                onChange={handleFormChange} 
                                                style={{ fontSize: '0.8rem' }}
                                            />
                                            <button className="btn btn-light text-muted border-0 bg-transparent px-3" type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                                {showConfirmPassword ? <FaEyeSlash size={14}/> : <FaEye size={14}/>}
                                            </button>
                                        </div>
                                        {formData.password !== formData.confirmPassword && formData.confirmPassword.length > 0 && (
                                            <div className="text-danger mt-1" style={{ fontSize: '0.65rem' }}>Password tidak cocok.</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="card-footer bg-white border-0 p-4 pt-0 d-flex justify-content-end gap-2">
                                <button type="button" className="btn btn-sm btn-light border fw-bold px-4 hover-scale" style={{ fontSize: '0.8rem' }} onClick={() => setShowModal(false)}>Batal</button>
                                <button type="submit" className="btn btn-sm btn-primary fw-bold px-4 shadow-sm d-flex align-items-center gap-2 hover-scale" style={{ fontSize: '0.8rem' }}>
                                    <FaSave /> {isEditMode ? 'Simpan' : 'Buat Akun'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>,
                document.body
            )}

            {/* PORTAL UNTUK MODAL CONFIRMATION (HAPUS) */}
            {confirmModal.show && createPortal(
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ zIndex: 1200, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(3px)' }}>
                    <div className="card border-0 shadow-lg animate-pop-in rounded" style={{ width: '90%', maxWidth: '380px' }}>
                        <div className="card-body p-4 text-center">
                            <div className="mb-3">
                                <div className="bg-danger bg-opacity-10 text-danger rounded-circle d-inline-flex p-3 shadow-sm">
                                    <FaExclamationTriangle size={30} />
                                </div>
                            </div>
                            <h6 className="fw-bold text-dark mb-2">{confirmModal.title}</h6>
                            <p className="text-muted mb-4" style={{ fontSize: '0.85rem' }}>{confirmModal.message}</p>
                            <div className="d-flex gap-2 justify-content-center">
                                <button className="btn btn-sm btn-light border fw-bold px-3 hover-scale" onClick={() => setConfirmModal({ show: false, title: '', message: '', action: null })}>Batal</button>
                                <button className="btn btn-sm btn-danger fw-bold text-white px-3 shadow-sm d-flex align-items-center gap-2 hover-scale" onClick={confirmModal.action}>
                                    <FaTrash /> Ya, Hapus
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* CSS GLOBAL INTERNAL */}
            <style>{`
                .cursor-pointer { cursor: pointer; }
                .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
                .animate-pop-in { animation: popIn 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275); }
                .transition-all { transition: all 0.2s ease-in-out; }
                .hover-scale { transition: transform 0.2s; }
                .hover-scale:hover { transform: scale(1.03); }
                .hover-bg-light:hover { background-color: #f8f9fa !important; }
                
                .custom-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }

                @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
                @keyframes popIn { 0% { transform: scale(0.95); opacity: 0; } 100% { transform: scale(1); opacity: 1; } }
            `}</style>
        </div>
    );
};

export default AdminUsers;